/**
 * Display-only currency for the book funnel (Phase 3). Does not affect Mews settlement.
 */

import type { Product } from "@/services/mews-api/types";

export const BOOK_DISPLAY_CURRENCY_STORAGE_KEY = "beatenberg-book-display-currency";

function norm(code: string): string {
  return code.trim().toUpperCase();
}

function isFiniteGrossBlock(block: unknown): boolean {
  if (!block || typeof block !== "object") return false;
  const g = (block as { GrossValue?: unknown }).GrossValue;
  return typeof g === "number" && Number.isFinite(g);
}

/** Currency keys on `Product.Pricing.Value` (multi-currency gross map). */
export function collectCurrencyKeysFromProducts(products: Product[]): string[] {
  const keys = new Set<string>();
  for (const product of products) {
    if (product.Pricing?.Discriminator !== "Absolute") continue;
    const value = product.Pricing.Value;
    if (!value || typeof value !== "object" || Array.isArray(value)) continue;
    for (const k of Object.keys(value as Record<string, unknown>)) {
      const block = (value as Record<string, unknown>)[k];
      if (isFiniteGrossBlock(block)) keys.add(norm(k));
    }
  }
  return [...keys];
}

/**
 * Stable order: follow `configuration/get` `CurrencyCodes` when provided, then any extras alphabetically.
 * Without order hint, sort codes alphabetically.
 */
export function sortCurrencySelectorOptions(
  codes: string[],
  enterpriseCurrencyOrder?: readonly string[] | null
): string[] {
  const upper = [...new Set(codes.map(norm))].filter(Boolean);
  if (!enterpriseCurrencyOrder?.length) {
    return [...upper].sort((a, b) => a.localeCompare(b));
  }
  const inSet = new Set(upper);
  const ordered: string[] = [];
  for (const c of enterpriseCurrencyOrder) {
    const n = norm(c);
    if (inSet.has(n) && !ordered.includes(n)) ordered.push(n);
  }
  const rest = upper.filter((c) => !ordered.includes(c)).sort((a, b) => a.localeCompare(b));
  return [...ordered, ...rest];
}
